import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Tema claro / oscuro del panel.
 *
 * Los profesionales atienden de noche: una pantalla blanca a las once cansa.
 *
 * OJO CON EL ALCANCE. El tema oscuro esta implementado en el panel del
 * profesional y en ningun otro lado: el sitio publico usa colores fijos de tema
 * claro (text-slate-900 y compania). Marcar la pagina entera como oscura le
 * cambia el fondo a todo pero no las letras, y el texto negro sobre fondo negro
 * queda literalmente invisible.
 *
 * Por eso este contexto SOLO guarda la preferencia. Quien decide aplicarla es
 * el armazon del panel, que se pinta a si mismo y limpia al salir.
 *
 * Tampoco se sigue 'prefers-color-scheme': nadie pidio el tema oscuro, y
 * activarlo solo porque el sistema operativo esta en oscuro le romperia el
 * sitio a una parte de las visitas sin que puedan desactivarlo (el interruptor
 * vive dentro del panel).
 */

const TemaContext = createContext({ tema: 'claro', alternar: () => {}, definir: () => {} });

const CLAVE = 'anonimamente_tema';

function temaInicial() {
  try {
    const guardado = localStorage.getItem(CLAVE);
    if (guardado === 'claro' || guardado === 'oscuro') return guardado;
  } catch {
    // Si el navegador bloquea el almacenamiento, se sigue en claro.
  }
  return 'claro';
}

export const TemaProvider = ({ children }) => {
  const [tema, setTema] = useState(temaInicial);

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE, tema);
    } catch {
      /* sin persistencia, el tema dura la sesion */
    }
  }, [tema]);

  const alternar = useCallback(() => {
    setTema((t) => (t === 'oscuro' ? 'claro' : 'oscuro'));
  }, []);

  return (
    <TemaContext.Provider value={{ tema, alternar, definir: setTema }}>
      {children}
    </TemaContext.Provider>
  );
};

export const useTema = () => useContext(TemaContext);

/**
 * Pinta la pagina con el tema elegido mientras el componente este montado, y la
 * devuelve a claro al desmontarse.
 *
 * La clase va en <html> y no en un contenedor porque asi tambien alcanza al
 * fondo de la pagina y a los dialogos y avisos, que React monta fuera del
 * arbol del panel. Al salir del panel se quita, y el sitio publico vuelve a
 * verse como fue disenado.
 */
export function useTemaEnLaPagina(tema) {
  useEffect(() => {
    const raiz = document.documentElement;
    const oscuro = tema === 'oscuro';

    raiz.classList.toggle('dark', oscuro);
    raiz.style.colorScheme = oscuro ? 'dark' : 'light';

    return () => {
      raiz.classList.remove('dark');
      raiz.style.colorScheme = 'light';
    };
  }, [tema]);
}

export default TemaProvider;
